import { QC_OUTCOMES, WESTGARD_RULES } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, reasonSchema } from './clinical-common.ts';

// Lab quality control (docs/plans/phase3-lab-qc-and-microbiology.md §1.1, §3; ADR 0039).
// QC values are not patient data. Means and SDs are what the lab entered; they cross as
// decimal strings and are parsed by @wodi/domain.

const nameSchema = z.string().trim().min(1).max(120);
const ACTIVE = ['active', 'inactive'] as const;
const decimalText = z.string().trim().min(1).max(20);
const isoDay = z.iso.date();

export const QC_SERIES_STATES = [
  'no_runs',
  'accepted',
  'warning',
  'rejected',
  'overridden',
] as const;
export type QcSeriesState = (typeof QC_SERIES_STATES)[number];

export const QC_SERIES_STATE_WORDS: Record<QcSeriesState, string> = {
  no_runs: 'No runs yet',
  accepted: 'In control',
  warning: 'Warning',
  rejected: 'Rejected',
  overridden: 'Override approved',
};

export const QC_OUTCOME_WORDS: Record<(typeof QC_OUTCOMES)[number], string> = {
  accepted: 'In control',
  warning: 'Warning',
  rejected: 'Rejected',
};

const namedRefSchema = z.object({ id: z.uuid(), name: z.string() });

export const qcTargetSchema = z.object({
  id: z.uuid(),
  lotId: z.uuid(),
  analyte: z.object({
    id: z.uuid(),
    name: z.string(),
    unit: z.string().nullable(),
    decimals: z.number().int(),
  }),
  analyzer: namedRefSchema.nullable(),
  /** As entered, without trailing zeros: "5.12". */
  mean: z.string(),
  sd: z.string(),
  source: z.string(),
  status: z.enum(['active', 'retired']),
  setBy: actorSchema,
  setAt: z.iso.datetime(),
});
export type QcTargetView = z.infer<typeof qcTargetSchema>;

export const qcLotSchema = z.object({
  id: z.uuid(),
  materialId: z.uuid(),
  lotNumber: z.string(),
  expiresOn: isoDay,
  /** Past its expiry day, East Africa Time. Runs are still recorded; the screen says so. */
  expired: z.boolean(),
  analyzerControlId: z.string().nullable(),
  status: z.enum(ACTIVE),
  statusReason: z.string().nullable(),
  targets: z.array(qcTargetSchema),
});
export type QcLotView = z.infer<typeof qcLotSchema>;

export const qcMaterialSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  levelLabel: z.string(),
  manufacturer: z.string().nullable(),
  status: z.enum(ACTIVE),
  lots: z.array(qcLotSchema),
});
export type QcMaterialView = z.infer<typeof qcMaterialSchema>;

export const qcRuleSettingSchema = z.object({
  analyteId: z.uuid(),
  analyteName: z.string(),
  rules: z.array(z.enum(WESTGARD_RULES)),
  /** No setting saved: the provisional default (plan Q1). */
  isDefault: z.boolean(),
});
export type QcRuleSettingView = z.infer<typeof qcRuleSettingSchema>;

export const qcSetupSchema = z.object({
  materials: z.array(qcMaterialSchema),
  ruleSettings: z.array(qcRuleSettingSchema),
  /** Numeric analytes a target can be set for. */
  analytes: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      unit: z.string().nullable(),
      decimals: z.number().int(),
    }),
  ),
  analyzers: z.array(namedRefSchema),
});
export type QcSetupView = z.infer<typeof qcSetupSchema>;

export const qcMaterialInputSchema = z.object({
  name: nameSchema,
  levelLabel: z.string().trim().min(1).max(40),
  manufacturer: z.string().trim().max(120).nullable().default(null),
});

export const updateQcMaterialSchema = qcMaterialInputSchema.extend({
  status: z.enum(ACTIVE),
});

export const qcLotInputSchema = z.object({
  lotNumber: z.string().trim().min(1).max(60),
  expiresOn: isoDay,
  /** The sample ID the analyzer reports for this control, if it reports one. */
  analyzerControlId: z.string().trim().min(1).max(64).nullable().default(null),
});

export const updateQcLotSchema = z.object({
  expiresOn: isoDay,
  analyzerControlId: z.string().trim().min(1).max(64).nullable(),
  status: z.enum(ACTIVE),
  statusReason: z.string().trim().min(5).max(500).nullable().default(null),
});

export const qcTargetInputSchema = z.object({
  analyteId: z.uuid(),
  /** Null: a manual or bench method, used for results entered by hand. */
  analyzerId: z.uuid().nullable(),
  mean: decimalText,
  sd: decimalText,
  /** Where the numbers came from, e.g. "Manufacturer insert" or "Our 20 runs, Sep 2026". */
  source: z.string().trim().min(3).max(200),
});

export const qcRulesInputSchema = z.object({
  rules: z.array(z.enum(WESTGARD_RULES)).max(WESTGARD_RULES.length),
});

// --- Runs -----------------------------------------------------------------------------------

export const qcOverrideSchema = z.object({
  id: z.uuid(),
  status: z.enum(['requested', 'approved', 'declined']),
  reason: z.string(),
  requestedBy: actorSchema,
  requestedAt: z.iso.datetime(),
  decidedBy: actorSchema.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
});
export type QcOverrideView = z.infer<typeof qcOverrideSchema>;

export const qcRunSchema = z.object({
  id: z.uuid(),
  targetId: z.uuid(),
  runAt: z.iso.datetime(),
  /** At the analyte's decimals. */
  value: z.string(),
  /** "+2.50" */
  sdIndex: z.string(),
  sdIndexHundredths: z.number().int(),
  mean: z.string(),
  sd: z.string(),
  outcome: z.enum(QC_OUTCOMES),
  violations: z.array(z.enum(WESTGARD_RULES)),
  rulesApplied: z.array(z.enum(WESTGARD_RULES)),
  source: z.enum(['manual', 'analyzer']),
  comment: z.string().nullable(),
  enteredBy: actorSchema,
  enteredAt: z.iso.datetime(),
  status: z.enum(['entered', 'entered_in_error']),
  statusReason: z.string().nullable(),
  correctiveActions: z.array(
    z.object({
      id: z.uuid(),
      note: z.string(),
      takenAt: z.iso.datetime(),
      recordedBy: actorSchema,
    }),
  ),
  overrides: z.array(qcOverrideSchema),
});
export type QcRunView = z.infer<typeof qcRunSchema>;

export const qcSeriesRowSchema = z.object({
  targetId: z.uuid(),
  analyte: z.object({ id: z.uuid(), name: z.string(), unit: z.string().nullable() }),
  material: z.object({ id: z.uuid(), name: z.string(), levelLabel: z.string() }),
  lot: z.object({
    id: z.uuid(),
    lotNumber: z.string(),
    expiresOn: isoDay,
    expired: z.boolean(),
  }),
  analyzer: namedRefSchema.nullable(),
  lastRun: z
    .object({
      id: z.uuid(),
      runAt: z.iso.datetime(),
      value: z.string(),
      sdIndex: z.string(),
      outcome: z.enum(QC_OUTCOMES),
    })
    .nullable(),
  state: z.enum(QC_SERIES_STATES),
  /** An override of the latest run is waiting for a second person. */
  overrideRequested: z.boolean(),
});
export type QcSeriesRowView = z.infer<typeof qcSeriesRowSchema>;

export const qcHoldSchema = z.object({
  analyte: namedRefSchema,
  analyzer: namedRefSchema.nullable(),
  runs: z.array(
    z.object({
      id: z.uuid(),
      targetId: z.uuid(),
      runAt: z.iso.datetime(),
      lotNumber: z.string(),
      levelLabel: z.string(),
    }),
  ),
});
export type QcHoldView = z.infer<typeof qcHoldSchema>;

export const qcSeriesListSchema = z.object({
  items: z.array(qcSeriesRowSchema),
  holds: z.array(qcHoldSchema),
});
export type QcSeriesListView = z.infer<typeof qcSeriesListSchema>;

export const qcSeriesSchema = z.object({
  target: qcTargetSchema,
  material: z.object({ id: z.uuid(), name: z.string(), levelLabel: z.string() }),
  lot: qcLotSchema.omit({ targets: true }),
  rules: z.array(z.enum(WESTGARD_RULES)),
  /** Oldest first; the latest 100. */
  runs: z.array(qcRunSchema),
  /** Observed, for display only; never a target. */
  statistics: z.object({
    n: z.number().int(),
    mean: z.string().nullable(),
    sd: z.string().nullable(),
    cvPercent: z.string().nullable(),
  }),
  /** Earlier targets of this lot, analyte and analyzer. */
  previousTargets: z.array(qcTargetSchema),
  held: z.boolean(),
});
export type QcSeriesView = z.infer<typeof qcSeriesSchema>;

export const recordQcRunSchema = z.object({
  targetId: z.uuid(),
  value: decimalText,
  /** When the control was measured; defaults to now. */
  runAt: z.iso.datetime({ offset: true }).optional(),
  comment: z.string().trim().max(500).nullable().default(null),
});

export const qcCorrectiveActionInputSchema = z.object({
  note: z.string().trim().min(5).max(2000),
  takenAt: z.iso.datetime({ offset: true }).optional(),
});

export const qcOverrideRequestSchema = z.object({ reason: reasonSchema });
export const qcOverrideDecisionSchema = z.object({
  note: z.string().trim().max(500).nullable().default(null),
});
